/** Maps marketplace category item slugs to admin ChatTopic.topic_key (thread category). */
export interface ServiceTopicRoute {
  topicKey: string;
  title: string;
  draftMessage?: string;
}

const EMERGENCY_TOPIC_BY_SLUG: Record<string, ServiceTopicRoute> = {
  'accident-help': { topicKey: 'emergency_accident_help', title: 'Accident Help' },
  'roadside-assist': { topicKey: 'emergency_roadside_assistance', title: 'Roadside Assistance' },
  towing: { topicKey: 'emergency_towing_assistance', title: 'Towing Assistance' },
  'vehicle-lockout': { topicKey: 'emergency_vehicle_lockout', title: 'Vehicle Lockout' },
};

const LEGAL_TOPIC_BY_SLUG: Record<string, ServiceTopicRoute> = {
  'traffic-ticket': { topicKey: 'legal_traffic_tickets', title: 'Traffic Tickets' },
  'legal-consult': { topicKey: 'legal_legal_consultation', title: 'Legal Consultation' },
  'fraud-disputes': { topicKey: 'legal_fraud_disputes', title: 'Fraud & Disputes' },
  immigration: { topicKey: 'legal_immigration', title: 'Immigration' },
};

const FINANCIAL_TOPIC_BY_SLUG: Record<string, ServiceTopicRoute> = {
  'tax-services': { topicKey: 'financial_tax_services', title: 'Tax Services' },
  'individual-tax-filing': { topicKey: 'financial_tax_services', title: 'Individual Tax Filing' },
  insurance: { topicKey: 'financial_insurance', title: 'Insurance' },
  'business-services': { topicKey: 'financial_business_services', title: 'Business Services' },
  'financial-svcs': { topicKey: 'financial_financial_services', title: 'Financial Services' },
};

const VEHICLE_TOPIC_BY_SLUG: Record<string, ServiceTopicRoute> = {
  'buy-vehicle': { topicKey: 'vehicle_buy_vehicle', title: 'Buy a Vehicle' },
  'sell-vehicle': { topicKey: 'vehicle_sell_vehicle', title: 'Sell Your Car' },
  financing: { topicKey: 'vehicle_financing', title: 'Financing' },
  'trade-in': { topicKey: 'vehicle_trade_in', title: 'Trade-In Estimate' },
};

const PROFESSIONALS_TOPIC_BY_SLUG: Record<string, ServiceTopicRoute> = {
  rcic: { topicKey: 'professionals_rcic', title: 'RCICs' },
  lawyers: { topicKey: 'professionals_lawyers', title: 'Lawyers' },
  accountants: { topicKey: 'professionals_accountants', title: 'Accountants' },
  paralegals: { topicKey: 'professionals_paralegals', title: 'Paralegals' },
};

export function getEmergencyTopicBySlug(slug: string): ServiceTopicRoute | null {
  return EMERGENCY_TOPIC_BY_SLUG[slug] ?? null;
}

export function getLegalTopicBySlug(slug: string): ServiceTopicRoute | null {
  return LEGAL_TOPIC_BY_SLUG[slug] ?? null;
}

export function getFinancialTopicBySlug(slug: string): ServiceTopicRoute | null {
  return FINANCIAL_TOPIC_BY_SLUG[slug] ?? null;
}

export function getVehicleTopicBySlug(slug: string): ServiceTopicRoute | null {
  return VEHICLE_TOPIC_BY_SLUG[slug] ?? null;
}

export function getProfessionalsTopicBySlug(slug: string): ServiceTopicRoute | null {
  return PROFESSIONALS_TOPIC_BY_SLUG[slug] ?? null;
}

export function resolveServiceTopic(categoryId: string, slug: string): ServiceTopicRoute | null {
  if (categoryId === 'emergency') {
    return getEmergencyTopicBySlug(slug);
  }
  if (categoryId === 'legal') {
    return getLegalTopicBySlug(slug);
  }
  if (categoryId === 'financial') {
    return getFinancialTopicBySlug(slug);
  }
  if (categoryId === 'vehicle') {
    return getVehicleTopicBySlug(slug);
  }
  if (categoryId === 'professionals') {
    return getProfessionalsTopicBySlug(slug);
  }
  return null;
}

export function buildChatTopicSearchParams(topic: ServiceTopicRoute): URLSearchParams {
  return new URLSearchParams({
    topicKey: topic.topicKey,
    title: topic.title,
  });
}

export function buildChatTopicPath(topic: ServiceTopicRoute): string {
  return `/chat?${buildChatTopicSearchParams(topic).toString()}`;
}

export function parseChatTopicFromSearchParams(params: URLSearchParams): ServiceTopicRoute | null {
  const topicKey = params.get('topicKey')?.trim();
  const title = params.get('title')?.trim();
  if (!topicKey || !title) return null;
  return { topicKey, title };
}
